import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Marquee from "../components/Marquee";
import Reveal from "../components/Reveal";
import { companies } from "../data/companies";

const HERO_TEXTURE =
  "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGVsZWdhbnQlMjBkYXJrJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODU2MzAwMjN8MA&ixlib=rb-4.1.0&q=85";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.3 + i * 0.18, ease: [0.7, 0, 0.2, 1] },
  }),
};

const chapters = [
  {
    num: "[01]",
    title: "Vision",
    body: "Fairmount Media Group builds and scales the media brands that shape professional culture. We turn attention into enduring influence.",
  },
  {
    num: "[02]",
    title: "Craft",
    body: "Every property in our portfolio is engineered for reach — editorial rigor, native distribution, and audiences measured in the millions.",
  },
  {
    num: "[03]",
    title: "Scale",
    body: "From a single channel to a network of voices, we compound audience, trust and revenue across platforms and formats.",
  },
];

const stats = [
  { value: "30M+", label: "Combined Followers" },
  { value: "2012", label: "Building Since" },
  { value: "10", label: "Media Properties" },
  { value: "∞", label: "Stories Told" },
  { value: "5", label: "Global Markets" },
  { value: "500M+", label: "Monthly Impressions" },
  { value: "40+", label: "Creators & Team" },
  { value: "12", label: "Content Verticals" },
];

const HeroLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span
      custom={i}
      variants={line}
      initial="hidden"
      animate="visible"
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const textureY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const textureScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const featured = companies[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative flex min-h-screen items-end overflow-hidden bg-[#070707] px-6 pb-20 md:px-12"
      >
        <motion.div
          style={{ y: textureY, scale: textureScale }}
          className="absolute inset-0 z-0"
        >
          <img
            src={HERO_TEXTURE}
            alt=""
            className="h-full w-full object-cover opacity-[0.18]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070707] via-[#070707]/60 to-[#070707]/30" />
        </motion.div>

        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative z-10 mx-auto w-full max-w-[1600px]"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mb-8 font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E83D16]"
          >
            [ Media Holding Company &mdash; Est. Global ]
          </motion.p>

          <h1 className="font-display text-[13vw] font-medium leading-[0.86] tracking-tighter text-[#F5F5F0] md:text-[10vw]">
            <HeroLine i={0}>Fairmount</HeroLine>
            <HeroLine i={1}>
              <span className="italic text-[#A1A1AA]">Media</span> Group
            </HeroLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
            className="mt-10 flex flex-col items-start gap-8 border-t border-[#27272A] pt-8 md:flex-row md:items-end md:justify-between"
          >
            <p className="max-w-md font-body text-base leading-relaxed text-[#A1A1AA] md:text-lg">
              We build, operate and scale the media brands that command the
              attention of the professional world.
            </p>
            <Link
              to="/portfolio"
              data-testid="hero-portfolio-cta"
              className="btn-editorial group inline-flex items-center gap-3 border border-[#F5F5F0] px-8 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#F5F5F0] hover:text-white"
            >
              View Portfolio
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* MANIFESTO */}
      <section
        data-testid="home-manifesto"
        className="bg-[#070707] px-6 py-28 md:px-12 md:py-40"
      >
        <div className="mx-auto max-w-[1600px]">
          <Reveal>
            <p className="mb-16 font-mono-accent text-xs uppercase tracking-[0.3em] text-[#A1A1AA]">
              Manifesto
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-12">
            {chapters.map((c, i) => (
              <Reveal
                key={c.num}
                delay={i * 0.1}
                className={
                  i === 0
                    ? "md:col-span-5"
                    : i === 1
                      ? "md:col-span-4 md:col-start-7"
                      : "md:col-span-4 md:col-start-8"
                }
              >
                <span className="font-mono-accent text-sm text-[#E83D16]">
                  {c.num}
                </span>
                <h3 className="mt-4 font-display text-4xl tracking-tight text-[#F5F5F0] md:text-5xl">
                  {c.title}
                </h3>
                <p className="mt-5 font-body text-base leading-relaxed text-[#A1A1AA]">
                  {c.body}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* STATS */}
      <section
        data-testid="home-stats"
        className="bg-[#070707] px-6 py-28 md:px-12 md:py-36"
      >
        <div className="mx-auto grid max-w-[1600px] grid-cols-2 gap-x-8 gap-y-16 md:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <p className="font-display text-6xl tracking-tight text-[#F5F5F0] md:text-7xl">
                {s.value}
              </p>
              <p className="mt-3 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">
                {s.label}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PORTFOLIO PREVIEW */}
      <section
        data-testid="home-portfolio-preview"
        className="border-t border-[#27272A] bg-[#070707] px-6 py-28 md:px-12 md:py-40"
      >
        <div className="mx-auto max-w-[1600px]">
          <div className="mb-16 flex items-end justify-between">
            <Reveal>
              <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E83D16]">
                Featured Property
              </p>
              <h2 className="mt-4 font-display text-5xl tracking-tighter text-[#F5F5F0] md:text-7xl">
                The Portfolio
              </h2>
            </Reveal>
          </div>

          <Link
            to={`/portfolio/${featured.id}`}
            data-testid="preview-company-confidential"
            className="group grid grid-cols-1 gap-8 border-t border-[#27272A] pt-10 md:grid-cols-12"
          >
            <div className="overflow-hidden md:col-span-5">
              <img
                src={featured.image}
                alt={featured.name}
                className="h-72 w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0 md:h-96"
              />
            </div>
            <div className="flex flex-col justify-between md:col-span-6 md:col-start-7">
              <div>
                <div className="flex items-center gap-4">
                  <h3 className="font-display text-5xl tracking-tight text-[#F5F5F0] transition-colors duration-300 group-hover:text-[#E83D16] md:text-6xl">
                    {featured.name}
                  </h3>
                  <ArrowUpRight
                    size={36}
                    className="text-[#A1A1AA] transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#E83D16]"
                  />
                </div>
                <p className="mt-4 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#A1A1AA]">
                  {featured.category} &mdash; {featured.platform}
                </p>
                <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-[#A1A1AA]">
                  {featured.tagline}
                </p>
              </div>
              <div className="mt-8 flex gap-12 border-t border-[#27272A] pt-6">
                <div>
                  <p className="font-display text-3xl text-[#F5F5F0]">
                    {featured.followers}
                  </p>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                    Followers
                  </p>
                </div>
                <div>
                  <p className="font-display text-3xl text-[#F5F5F0]">
                    {featured.founded}
                  </p>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                    Founded
                  </p>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
